// Entry to /providers/apply: capture the therapist's email, call /start, then show
// the check-your-email handoff. Outside production the API echoes the apply link so
// staging can continue in-place — surfaced as a clearly-labelled convenience link.
//
// Arriving from the directory claim flow (?npi=…&country=…, lib/claimHandoff.ts)
// the screen says so and sends the listing's id with the email, so the draft
// starts linked to the claimed listing. Same form, same endpoint otherwise.
import { useMemo, useState } from 'react';
import { PrimaryButton, card } from '../components/ui';
import { ProviderShell, ScreenHeader } from './ProviderShell';
import { startApplication, type StartResult } from '../lib/providerApi';
import { RateLimited } from '../lib/api';
import { track } from '../lib/analytics';
import { readClaimHandoff, registryLabel } from '../lib/claimHandoff';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function StartScreen() {
  const handoff = useMemo(() => readClaimHandoff(), []);
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<StartResult | null>(null);

  async function send() {
    setSending(true);
    setError(null);
    try {
      const res = await startApplication(email.trim(), handoff);
      setResult(res);
      track('provider_apply_email_sent', { from_claim: Boolean(handoff) });
    } catch (e) {
      if (e instanceof RateLimited) {
        setError('Too many attempts from this connection. Give it a few minutes and try again.');
      } else {
        setError("We couldn't send the link just now. Check your connection and try again.");
      }
    } finally {
      setSending(false);
    }
  }

  if (result) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ maxWidth: 460, textAlign: 'center' }}>
          <div style={{ width: 64, height: 64, borderRadius: 18, background: 'var(--teal-tint)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 22px' }}>
            <svg width={30} height={30} viewBox="0 0 24 24" fill="none" aria-hidden>
              <rect x={3} y={5} width={18} height={14} rx={3} stroke="var(--teal-deep)" strokeWidth={1.8} />
              <path d="M4 7l8 6 8-6" stroke="var(--teal-deep)" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div style={{ fontSize: 30, fontWeight: 800 }}>Check your email.</div>
          <div className="pretty" style={{ fontSize: 16, fontWeight: 500, color: 'var(--slate)', marginTop: 12, lineHeight: 1.55 }}>
            We sent your application link to <strong style={{ color: 'var(--ink)' }}>{email.trim()}</strong>. It saves as
            you go, so you can start now and finish later from the same link.
            {handoff && ' Your listing details are already filled in where we have them.'}
          </div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--slate)', marginTop: 20 }}>
            Didn't get it? Check spam, or{' '}
            <button onClick={() => setResult(null)} style={{ color: 'var(--teal-deep)', fontWeight: 800 }}>
              try a different email
            </button>
            .
          </div>
          {result.applyLink && (
            <div style={{ marginTop: 26, fontSize: 13, fontWeight: 600, color: 'var(--slate)' }}>
              <a href={result.applyLink} style={{ fontWeight: 800 }}>
                Continue now without the email
              </a>
              <div style={{ marginTop: 4, opacity: 0.8 }}>(staging convenience — the link is only shown outside production)</div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <ProviderShell progress={0.04} rightLabel="About 10 min" footnote="Everything saves as a draft. Come back any time from your email link.">
      {handoff ? (
        <ScreenHeader
          eyebrow="Complete your profile"
          title="Finish your Expy profile."
          subtitle={`You claimed the listing for ${registryLabel(handoff)}. The claim form only asked what we need to verify you. Add your bio, specialties, insurance and rates so patients can choose you.`}
        />
      ) : (
        <ScreenHeader
          eyebrow="Join Expy Health"
          title="Apply to see patients on Expy."
          subtitle="Tell us about your practice and licensing. We review every application and verify every license before your profile goes live."
        />
      )}
      <div style={{ ...card, padding: '6px 8px' }}>
        <input
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && EMAIL_RE.test(email.trim()) && !sending) void send();
          }}
          placeholder="you@yourpractice.com"
          style={{ border: 'none', outline: 'none', width: '100%', padding: '14px 12px', fontSize: 16, fontWeight: 600, background: 'transparent' }}
        />
      </div>
      <div className="pretty" style={{ fontSize: 13, fontWeight: 600, color: 'var(--slate)', marginTop: 12, lineHeight: 1.5 }}>
        We'll email you a private link to your application. No password needed.
      </div>
      <div style={{ marginTop: 24 }}>
        <PrimaryButton full disabled={!EMAIL_RE.test(email.trim()) || sending} onClick={() => void send()}>
          {sending ? 'Sending…' : 'Email me my application link'}
        </PrimaryButton>
      </div>
      {error && (
        <div style={{ marginTop: 14, fontSize: 13, fontWeight: 700, color: '#C2554F' }}>{error}</div>
      )}
    </ProviderShell>
  );
}
